import {
  createArticleOrder,
  updateArticleOrder,
  createRailRodOrder
} from "@/utils"
import submitButton from './submitButton'
import { PRODUCT_DEFAULTS } from "@/constants";

const getDefaultState = (priceFetchStatus = true) => {
  return {
    // id of the curtain article order being edited
    curtainOrderNumber: undefined,
    // id of the rail rod article order being edited
    railRodOrderNumber: undefined,
    // the room in which the curtain/accessory should be placed
    room: '',
    // main fabric for a curtain / sample
    fabric: {},
    // lining fabric for a curtain
    lining: {},
    // can store multiple sample fabrics
    fabrics: [],
    // type of the curtain if applicable
    curtainType: undefined,
    // type of the rail/rod if applicable
    railRodType: '',
    // railRod just saves a string "rod" for ring curtain,
    // or "rail" for other curtains
    railRod: 'rail',
    // parameters which can be used to calculate a curtain
    // price or save order data
    curtainParameters: {},
    // the price object returned by the curtain price endpoints
    curtainPriceData: {},
    // the amount of curtain articles that should be created
    // on completing the flow
    curtainQuantity: 1,
    // the flow of the curtain that should be mapped from
    // order data and loaded into the CurtainFlow component
    curtainFlow: {},
    // the template containing information about the possible
    // options for a certain curtain type
    curtainTemplate: {},
    // converted vee-validate field rules from curtain template
    curtainValidations: {},
    // the template containing information about the possible
    // options for a certain rail rod type
    railRodTemplate: {},
    // the flow of the rail rod that should be mapped from
    // order data and loaded into the RailRodFlow component
    railRodFlow: {},
    // parameters which can be used to calculate a rail rod
    // price or save order data
    railRodParameters: {},
    // the price object returned by the rail rod price endpoints
    railRodPriceData: {},
    // the amount of rail rod articles that should be created
    // on completing the flow
    railRodQuantity: 1,
    // the price type to show, default - sale price
    priceType: 'price',
    draftOrderId: undefined,
    withRailRod: null,
    articles: [],
    // do price requests automatically is true
    priceFetchStatus,
    resetKey: 1,
    dropError:'',
  }
}

// initial state
const state = getDefaultState()

// getters
const getters = {
  getRoom(state) {
    return state.room
  },
  getFabric(state) {
    return state.fabric
  },
  getLining(state) {
    return state.lining
  },
  getFabrics(state) {
    return state.fabrics
  },
  getCurtainType(state) {
    return state.curtainType
  },
  getRailRodType(state) {
    return state.railRodType
  },
  getRailRod(state) {
    return state.railRod
  },
  getCurtainParameters(state) {
    return state.curtainParameters
  },
  getCurtainPriceData(state) {
    return state.curtainPriceData
  },
  getCurtainQuantity(state) {
    return state.curtainQuantity
  },
  getCurtainFlow(state) {
    return state.curtainFlow
  },
  getCurtainTemplate(state) {
    return state.curtainTemplate
  },
  getCurtainValidations(state) {
    return state.curtainValidations
  },
  getRailRodTemplate(state) {
    return state.railRodTemplate
  },
  getRailRodFlow(state) {
    return state.railRodFlow
  },
  getRailRodParameters(state) {
    return state.railRodParameters
  },
  getRailRodPriceData(state) {
    return state.railRodPriceData
  },
  getRailRodQuantity(state) {
    return state.railRodQuantity
  },
  getPriceType(state) {
    return state.priceType
  },
  getOrderId(state) {
    return state.draftOrderId
  },
  getWithRailRod(state) {
    return state.withRailRod
  },
  getArticles(state) {
    return state.articles
  },
  getCartData(state) {
    return {
      articles: state.articles.map(number => {
        return { orderNumber: number }
      })
    }
  },
  getPriceFetchStatus(state) {
    return state.priceFetchStatus
  },
  hasCurtainPrices(state) {
    const prices = state.curtainPriceData.prices
    return !!prices && !!prices.price
  },
  getCurtainPrice(state, getters) {
    if (!getters['hasCurtainPrices']) return 0
    return parseFloat(state.curtainPriceData.prices.price)
  },
  hasCurtainDiscountPrice(state, getters) {
    if (!getters['hasCurtainPrices']) return false
    const discountPrices = state.curtainPriceData.prices.discount
    return !!discountPrices && !!discountPrices.discount_price && !!discountPrices.origin_price
  },
  getCurtainDiscountPrice(state, getters) {
    if (getters['hasCurtainDiscountPrice']) {
      return parseFloat(state.curtainPriceData.prices.discount.discount_price)
    }
    return 0
  },
  getCurtainOriginPrice(state, getters) {
    if (getters['hasCurtainDiscountPrice']) {
      return parseFloat(state.curtainPriceData.prices.discount.origin_price)
    }
    return 0
  },
  hasRailRodPrices(state) {
    const prices = state.railRodPriceData.prices
    return !!prices && !!prices.price
  },
  getRailRodPrice(state, getters) {
    if (!getters['hasRailRodPrices'] || !state.withRailRod) return 0
    return parseFloat(state.railRodPriceData.prices.price)
  },
  hasRailRodDiscountPrice(state, getters) {
    if (!getters['hasRailRodPrices']) return false
    const discountPrices = state.railRodPriceData.prices.discount
    return !!discountPrices && !!discountPrices.discount_price && !!discountPrices.origin_price
  },
  getRailRodDiscountPrice(state, getters) {
    if (getters['hasRailRodDiscountPrice']) {
      return parseFloat(state.railRodPriceData.prices.discount.discount_price)
    }
    return 0
  },
  getRailRodOriginPrice(state, getters) {
    if (getters['hasRailRodDiscountPrice']) {
      return parseFloat(state.railRodPriceData.prices.discount.origin_price)
    }
    return 0
  },
  getResetKey(state) {
    return state.resetKey
  },
  hasDiscountPrice(state, getters) {
    return getters['hasCurtainDiscountPrice'] || getters['hasRailRodDiscountPrice']
  },
  getDropError(state) {
    return state.dropError
  },
}

// mutations
const mutations = {
  setCurtainOrderNumber(state, curtainOrderNumber) {
    state.curtainOrderNumber = curtainOrderNumber
  },
  setRailRodOrderNumber(state, railRodOrderNumber) {
    state.railRodOrderNumber = railRodOrderNumber
  },
  setRoom(state, room) {
    state.room = room
  },
  addFabric(state, fabricDetails) {
    // add fabrics to multiselect fabric picker
    state.fabrics = [...state.fabrics, fabricDetails]
  },
  deleteFabric(state, fabricUuid) {
    // remove fabrics from multiselect fabric picker
    state.fabrics = state.fabrics.filter(
      selectedFabric => selectedFabric.fabric_uuid !== fabricUuid
    )
  },
  setFabric(state, fabricDetails) {
    // set fabric for single select fabric picker
    state.fabric = fabricDetails
  },
  setLining(state, fabricDetails) {
    // set lining for single select fabric picker
    state.lining = fabricDetails
  },
  setCurtainType(state, curtainType) {
    state.curtainType = curtainType
  },
  setFoldType(state, foldType) {
    state.foldType = foldType
  },
  setRailRodType(state, railRodType) {
    state.railRodType = railRodType
  },
  setWithLining(state, withLining) {
    state.withLining = withLining
  },
  setWithMemo(state, withMemo) {
    state.withMemo = withMemo
  },
  setWithRailRod(state, withRailRod) {
    state.withRailRod = withRailRod
  },
  setRailRod(state, railRod) {
    state.railRod = railRod
  },
  setCurtainParameters(state, curtainParameters) {
    state.curtainParameters = curtainParameters
  },
  addCurtainParameter(state, curtainParameter) {
    state.curtainParameters = {
      ...state.curtainParameters,
      ...curtainParameter
    }
  },
  addCurtainTemplateParameter(state, curtainTemplateParameter) {
    state.curtainTemplate = {
      ...state.curtainTemplate,
      ...curtainTemplateParameter
    }
  },
  setCurtainValidations(state, validations) {
    state.curtainValidations = validations
  },
  setCurtainPriceData(state, curtainPriceData) {
    state.curtainPriceData = curtainPriceData
  },
  setCurtainQuantity(state, curtainQuantity) {
    state.curtainQuantity = curtainQuantity
  },
  setCurtainFlow(state, curtainFlow) {
    state.curtainFlow = curtainFlow
  },
  setCurtainTemplate(state, curtainTemplate) {
    state.curtainTemplate = curtainTemplate
  },
  setRailRodTemplate(state, railRodTemplate) {
    state.railRodTemplate = railRodTemplate
  },
  setRailRodFlow(state, railRodFlow) {
    state.railRodFlow = railRodFlow
  },
  setRailRodParameters(state, railRodParameters) {
    state.railRodParameters = railRodParameters
  },
  addRailRodParameter(state, railRodParameter) {
    state.railRodParameters = {
      ...state.railRodParameters,
      ...railRodParameter
    }
  },
  setRailRodPriceData(state, railRodPriceData) {
    state.railRodPriceData = railRodPriceData
  },
  setRailRodQuantity(state, railRodQuantity) {
    state.railRodQuantity = railRodQuantity
  },
  resetRailRodSupports(state) {
    delete state.railRodParameters.support_type
    delete state.railRodParameters.support_size
    delete state.railRodParameters.additional_support_type
    delete state.railRodParameters.additional_support_size
  },
  resetState(state) {
    let key = state.resetKey + 1
    Object.assign(state, getDefaultState())
    state.resetKey = key
  },
  setPriceType(state, priceType) {
    state.priceType = priceType
  },
  setDraftOrderId(state, orderId) {
    state.draftOrderId = orderId
  },
  setArticles(state, articles) {
    state.articles = articles
  },
  addArticle(state, article) {
    state.articles.push(article)
  },
  enablePriceFetch(state) {
    state.priceFetchStatus = true
  },
  disablePriceFetch(state) {
    state.priceFetchStatus = false
  },
  setDropError(state, data) {
    state.dropError = data
  },
}

// actions
const actions = {
  setRoom({ commit }, room) {
    commit('setRoom', room)
  },
  setCurtainType({ commit }, curtainType) {
    commit('setCurtainType', curtainType)
  },
  setFoldType({ commit }, foldType) {
    commit('setFoldType', foldType)
  },
  setRailRodType({ commit }, railRodType) {
    commit('setRailRodType', railRodType)
  },
  setRailRod({ commit }, railRod) {
    commit('setRailRod', railRod)
  },
  setWithLining({ commit }, withLining) {
    commit('setWithLining', withLining)
  },
  setWithMemo({ commit }, withMemo) {
    commit('setWithMemo', withMemo)
  },
  async setWithRailRod({ state, commit, dispatch }, withRailRod) {
    if (state.withRailRod === true && withRailRod === false) {
      await dispatch('resetRailRod', {withRailRod, railRodType: state.railRodType})
    }
    commit('setWithRailRod', withRailRod)
  },
  addFabric({ commit }, { fabricDetails }) {
    commit('addFabric', fabricDetails)
  },
  deleteFabric({ commit }, { fabricUuid }) {
    commit('deleteFabric', fabricUuid)
  },
  setFabric({ commit }, { fabricDetails }) {
    commit('setFabric', fabricDetails)
  },
  setLining({ commit }, { fabricDetails }) {
    commit('setLining', fabricDetails)
  },
  resetLining({ commit }) {
    commit('setLining', {})
  },
  addCurtainParameter({ commit }, curtainParameter) {
    commit('addCurtainParameter', curtainParameter)
  },
  addCurtainTemplateParameter({ commit }, curtainTemplateParameter) {
    commit('curtainTemplateParameter', curtainTemplateParameter)
  },
  setCurtainParameters({ commit }, curtainParameters) {
    commit('setCurtainParameters', curtainParameters)
  },
  setCurtainPriceData({ commit }, curtainPriceData) {
    commit('setCurtainPriceData', curtainPriceData)
  },
  setCurtainQuantity({ commit }, curtainQuantity) {
    commit('setCurtainQuantity', curtainQuantity)
  },
  setCurtainFlow({ commit }, curtainFlow) {
    commit('setCurtainFlow', curtainFlow)
  },
  setCurtainTemplate({ commit }, curtainTemplate) {
    commit('setCurtainTemplate', curtainTemplate)
  },
  setRailRodTemplate({ commit }, railRodTemplate) {
    commit('setRailRodTemplate', railRodTemplate)
  },
  setRailRodFlow({ commit }, railRodFlow) {
    commit('setRailRodFlow', railRodFlow)
  },
  addRailRodParameter({ commit }, railRodParameter) {
    commit('addRailRodParameter', railRodParameter)
  },
  setRailRodPriceData({ commit }, railRodPriceData) {
    commit('setRailRodPriceData', railRodPriceData)
  },
  setRailRodQuantity({ commit }, railRodQuantity) {
    commit('setRailRodQuantity', railRodQuantity)
  },
  resetOrder( { dispatch }) {
    dispatch('resetCurtain')
    dispatch('resetRailRod')
  },
  resetCurtain({ commit }) {
    commit('setCurtainTemplate', {})
    commit('setCurtainParameters', {})
    commit('setCurtainPriceData', {})
    commit('setCurtainQuantity', 1)
    commit('setLining', {})
    commit('setCurtainType', undefined)
    commit('setFoldType', undefined)
    commit('setWithMemo', false)
    commit('setWithLining', false)
  },
  resetRailRod({ commit }, { railRodType = '', withRailRod = null } = {}) {
    commit('setRailRodTemplate', {})
    commit('setRailRodFlow', {})
    commit('setRailRodParameters', {})
    commit('setRailRodPriceData', {})
    commit('setRailRodQuantity', 1)
    commit('setRailRodType', railRodType)
    commit('setWithRailRod', withRailRod)
  },
  resetRailRodSupports({ commit }) {
    commit('resetRailRodSupports')
  },
  resetState({ commit }) {
    commit('resetState')
  },
  setPriceType({ commit }, priceType) {
    commit('setPriceType', priceType)
  },
  async loadAndSetFabric({ commit, dispatch, rootGetters }, fabricUuid) {
    await dispatch(
      'fabrics/loadFabricDetails',
      { urlParams: { fabricUuid } },
      { root: true }
    )
    const fabricDetails = rootGetters['fabrics/getFabricDetails']
    if (fabricDetails) {
      const fabric = rootGetters['fabrics/getFabricDetails'][fabricUuid]
      commit('setFabric', fabric)
    }
  },
  // get methods can be used for the edit flow, where we need to load
  // order data into the vuex state initially
  async createCurtainOrder({ state, commit, dispatch }, room) {
    const curtainParameters = { ...state.curtainParameters, room }
    const curtainOrderNumber = await createArticleOrder(state.curtainType, curtainParameters)
    if (state.withRailRod) {
      const railRodParameters = { ...state.railRodParameters, room, curtain_order_number: curtainOrderNumber }
      const railRodOrderNumber = await createRailRodOrder(state.railRodType, railRodParameters)
      if (!railRodOrderNumber) {
        console.error('rail or rod wasnt created')
      }
    }
    if (curtainOrderNumber) {
      commit('addArticle', curtainOrderNumber)
    } else {
      console.error('There was an error while creating or updating a curtain order')
    }
    return curtainOrderNumber
  },
  async updateRailRodOrder({ state }) {
    const railRodParameters = {
      ...state.railRodParameters,
      curtain_order_number: state.curtainOrderNumber,
      support_size: 'size35mm',
      room: state.room
    }
    return await updateArticleOrder(state.railRodOrderNumber, state.railRodType, railRodParameters)
  },

  async updateCurtainOrder({ state }) {
    const curtainParameters = {
      ...state.curtainParameters,
      ...PRODUCT_DEFAULTS[state.curtainType],
      room: state.room
    }
    return await updateArticleOrder(state.curtainOrderNumber, state.curtainType, curtainParameters)
  },

}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
  modules: {
    submitButton
  }
}
